import { type FunctionComponent, useState } from 'react';

import { Button, Link } from '@dr.pogodin/react-utils';

import Avatar from 'components/Avatar';
import { PractitionerIntroVideoModal } from 'components/PractitionerIntroVideoModal';
import type { ArtistT } from 'utils';

import IconCross from 'assets/images/icon-cross-16px.svg';

import infoButtonTheme from 'themes/Button/info-icon-button-grey.scss';

import './style.scss';

type PropsT = {
  artist: ArtistT | null;
  onClose: () => void;
};

export const Practitioner: FunctionComponent<PropsT> = ({
  artist,
  onClose,
}) => {
  const [showIntroVideo, setShowIntroVideo] = useState(false);

  if (!artist) {
    return (
      <div styleName="container">
        <div styleName="header">
          <div styleName="info">
            <h1 aria-label="No practitioner selected">No practitioner selected</h1>
            <div styleName="meta" />
          </div>
          <Button onClick={onClose} theme={infoButtonTheme}>
            <IconCross fill="white" />
          </Button>
        </div>
      </div>
    );
  }

  const name = `${artist.user.firstName} ${artist.user.lastName}`;
  const meta = [
    ...artist.modalities ?? [],
    ...artist.customModalities ?? [],
  ];

  const renderSocialLinks = () => (
    <div styleName="links">
      {!!artist.website && (
        <Link openNewTab to={artist.website}>
          Website
        </Link>
      )}
      {!!artist.instagram && (
        <Link
          openNewTab
          to={`https://instagram.com/${artist.instagram.startsWith('@') ? artist.instagram.slice(1) : artist.instagram}`}
        >
          Instagram
        </Link>
      )}
      {!!artist.tikTok && (
        <Link
          openNewTab
          to={`https://tiktok.com/${artist.tikTok.startsWith('@') ? artist.tikTok : `@${artist.tikTok}`}`}
        >
          TikTok
        </Link>
      )}
      {!!artist.youtube && (
        <Link
          openNewTab
          to={`https://youtube.com/${artist.youtube.startsWith('@') ? artist.youtube : `@${artist.youtube}`}`}
        >
          YouTube
        </Link>
      )}
    </div>
  );

  return (
    <div styleName="container">
      <div styleName="header">
        <Avatar user={artist.user} />
        <div styleName="info">
          <h1>{name}</h1>
          <div styleName="meta">{meta.join(' • ')}</div>
        </div>
        <Button onClick={onClose} theme={infoButtonTheme}>
          <IconCross fill="white" />
        </Button>
      </div>
      <div styleName="content">
        {renderSocialLinks()}
        {!!artist.videoIsReadyToBePlayed && (
          <div styleName="watchIntroVideo">
            <div
              onClick={() => {
                setShowIntroVideo(true);
              }}
              role="presentation"
              styleName="content"
            >
              <img
                alt="Thumbnail"
                src={artist.videoThumbnailUrl ?? ''}
                styleName="thumbnail"
              />
              <div>
                Watch intro video
              </div>
            </div>
          </div>
        )}

        <div styleName="viewFullProfile">
          <Link
            disabled
            to=""
          >
            View full profile
          </Link>
        </div>
      </div>
      {showIntroVideo ? (
        <PractitionerIntroVideoModal
          artist={artist}
          onClose={() => {
            setShowIntroVideo(false);
          }}
        />
      ) : null}
    </div>
  );
};
